import { Theme } from "@mui/material";
import { createStyles, WithStyles, withStyles } from "@mui/styles";
import { ICellEditorParams } from "ag-grid-community";
import { observer } from "mobx-react";
import * as React from "react";
import ComboBoxOption from "../../coreui/ComboBoxOption";
import { ErrorBadge } from "../../coreui/ErrorBadge";
import Select from "../../coreui/Select";
import { TableChildProps } from "../../coreui/Table";
import PaneRow from "../../models/PaneRow";
import ErrorsStore from "../../stores/ErrorsStore";
import { AccessLevel } from "../AccessLevel";
import { GridColumnConfigProperties } from "./GridColumn";

interface ConfigProperties extends ICellEditorParams<PaneRow> {
  dataId: string;
  name: string;
  onValueChange: (value: string | null | undefined) => void;
  propagated: TableChildProps;
}

interface RuntimeProperties {
  accessLevel: AccessLevel;
  businessErrors: string[];
  selectedDisplayValue: string;
  selectedValue: string | null;
}

const styles = (theme: Theme) =>
  createStyles({
    icon: {
      right: 19,
    },
  });

export class RelationshipComboBoxColumnEdit extends React.Component<
  ConfigProperties & WithStyles<typeof styles>
> {
  private get column(): GridColumnConfigProperties | undefined {
    const parentTable = this.props.propagated.parentTable;
    const tableColumns = parentTable.columns as GridColumnConfigProperties[];
    return tableColumns.find((c) => c.name === this.props.colDef.colId);
  }

  private onChange = (value: ComboBoxOption | null) => {
    ErrorsStore.clearBusinessErrorsForTableCell(
      this.props.dataId,
      this.props.name,
      this.props.data.rowKey
    );
    this.props.onValueChange(value?.value);
  };

  private onClose = () => {
    // Must be deferred to run after focus.
    setTimeout(() => {
      const focusedCell = this.props.api!.getFocusedCell();

      this.props.api!.stopEditing();

      if (focusedCell) {
        this.props.api!.setFocusedCell(
          focusedCell.rowIndex,
          focusedCell.column,
          focusedCell.rowPinned || undefined
        );
      }
    }, 500);
  };

  private onFocus = () => {
    const parentTable = this.props.propagated.parentTable;
    parentTable.setStopEditingWhenGridLosesFocus(false);
  };

  public componentWillUnmount(): void {
    this.props.propagated.parentTable.setStopEditingWhenGridLosesFocus(true);
  }

  public render(): React.ReactNode {
    const row = this.props.data!;
    const widget = row.getWidgetT<string | null, RuntimeProperties>(
      this.props.name
    );

    const selectedOption: ComboBoxOption = {
      display: widget.properties.selectedDisplayValue,
      value: widget.properties.selectedValue,
    };

    const table = this.props.propagated.parentTable;
    const options = table.getRelationshipComboBoxOptions(
      this.props.name,
      selectedOption
    );

    const errors: string[] = row.hasChanges(this.props.name)
      ? []
      : [...widget.properties.businessErrors];

    return (
      <ErrorBadge errors={errors} isShort={row.isNew && table.isDocumentGrid}>
        <Select
          aria-label={this.column?.header}
          disableUnderline={true}
          getErrors={() => errors}
          iconClassName={this.props.classes.icon}
          inputBaseStyle={{
            cursor: "pointer",
            display: "flex",
            height: "calc(100% + 2px)",
          }}
          onClose={this.onClose}
          onFocus={this.onFocus}
          onValueChange={this.onChange}
          openOnMount
          options={options}
          selectDisplayStyle={{
            lineHeight: `${this.props.node.rowHeight}px`,
            paddingBottom: 0,
            paddingLeft: 24,
            paddingTop: 0,
          }}
          showAsMandatory={this.column?.required}
          value={this.props.value}
        />
      </ErrorBadge>
    );
  }
}

export default withStyles(styles)(observer(RelationshipComboBoxColumnEdit));
